// hooks
import { type Pagination, useCommonQuery } from 'hooks';

// modules
import type {
  ReqWorkerList,
  ReqWorkerSafetyHistory,
  ReqWorkersNotInSafetyTraining,
  WorkerInfo,
  WorkerSafetyHistory
} from '../model/worker.model';
import type {
  BaseApiResponse,
  CommonQueryOptions,
  ReqGetUserDetail,
  ResultError,
  UserCombinedInfo
} from 'modules';

// apis
import { WorkerAPI } from '../apis/worker.api';

// keys
import { workerKeys } from '../keys/worker.key';

export const workerApi = new WorkerAPI();

/** 작업자 목록 조회 쿼리 훅 */
export const useGetWorkerList = ({
  data,
  options
}: {
  data: ReqWorkerList;
  options?: CommonQueryOptions<BaseApiResponse<unknown, WorkerInfo>, ResultError, WorkerInfo[]>;
}) => {
  const queryKeyFactory = (data: ReqWorkerList) => workerKeys.GET_WORKER_LIST(data);

  const queryFn = async (data: ReqWorkerList, pagination: Pagination) => {
    const req = {
      ...data,
      page: pagination.current,
      size: pagination.pageSize
    };

    return await workerApi.getWorkerList(req);
  };

  return useCommonQuery<
    ReqWorkerList,
    BaseApiResponse<unknown, WorkerInfo>,
    ResultError,
    WorkerInfo[]
  >({
    data,
    options: {
      ...options,
      select: (data) => data.resultList
    },
    queryKeyFactory,
    queryFn
  });
};

/** 작업자 상세 조회 쿼리 훅 */
export const useGetWorkerId = ({
  data,
  options
}: {
  data: ReqGetUserDetail;
  options?: CommonQueryOptions<BaseApiResponse<UserCombinedInfo>, ResultError, UserCombinedInfo>;
}) => {
  const queryKeyFactory = (data: ReqGetUserDetail) => workerKeys.GET_WORKER_DETAIL(data);

  const queryFn = async (data: ReqGetUserDetail) => {
    return await workerApi.getWorkerDetail(data);
  };

  return useCommonQuery<
    ReqGetUserDetail,
    BaseApiResponse<UserCombinedInfo>,
    ResultError,
    UserCombinedInfo
  >({
    data,
    options: {
      ...options,
      select: (data) => data.resultObject
    },
    queryKeyFactory,
    queryFn
  });
};

/** 작업자 상세 조회 쿼리 훅 */
export const useGetWorkerSafetyHistory = ({
  data,
  options
}: {
  data: ReqWorkerSafetyHistory;
  options?: CommonQueryOptions<
    BaseApiResponse<unknown, WorkerSafetyHistory>,
    ResultError,
    WorkerSafetyHistory[]
  >;
}) => {
  const queryKeyFactory = (data: ReqWorkerSafetyHistory) =>
    workerKeys.GET_WORKER_SAFETY_HISTORY(data);

  const queryFn = async (data: ReqWorkerSafetyHistory, pagination: Pagination) => {
    const req = {
      ...data,
      page: pagination.current,
      size: pagination.pageSize
    };

    const query = Object.fromEntries(
      Object.entries(req).filter(([_, value]) => value !== '')
    ) as ReqWorkerSafetyHistory;

    return await workerApi.getWorkerSafetyHistory(query);
  };

  return useCommonQuery<
    ReqWorkerSafetyHistory,
    BaseApiResponse<unknown, WorkerSafetyHistory>,
    ResultError,
    WorkerSafetyHistory[]
  >({
    data,
    options: {
      ...options,
      select: (data) => data.resultList
    },
    queryKeyFactory,
    queryFn
  });
};

/** 작업자/관리자 목록 전체 조회 쿼리 훅 */
export const useGetWorkerAndAdminList = ({
  data,
  options
}: {
  data: ReqWorkerList;
  options?: CommonQueryOptions<BaseApiResponse<unknown, WorkerInfo>, ResultError, WorkerInfo[]>;
}) => {
  const queryKeyFactory = (data: ReqWorkerList) => workerKeys.GET_WORKER_AND_ADMIN_LIST(data);

  const queryFn = async (data: ReqWorkerList, pagination: Pagination) => {
    const req = {
      ...data,
      page: pagination.current,
      size: pagination.pageSize
    };

    const query = Object.fromEntries(
      Object.entries(req).filter(([_, value]) => value !== '')
    ) as ReqWorkerList;

    return await workerApi.getWorkerAndAdminList(query);
  };

  return useCommonQuery<
    ReqWorkerList,
    BaseApiResponse<unknown, WorkerInfo>,
    ResultError,
    WorkerInfo[]
  >({
    data,
    options: {
      ...options,
      select: (data) => data.resultList
    },
    queryKeyFactory,
    queryFn
  });
};

/** 안전교육에 참여하지 않은 사용자 목록 조회 쿼리 훅 */
export const useGetWorkersNotInSafetyTraining = ({
  data,
  options
}: {
  data: ReqWorkersNotInSafetyTraining;
  options?: CommonQueryOptions<BaseApiResponse<unknown, WorkerInfo>, ResultError, WorkerInfo[]>;
}) => {
  const queryKeyFactory = (data: ReqWorkersNotInSafetyTraining) =>
    workerKeys.GET_WORKERS_NOT_IN_SAFETY_TRAINING(data);

  const queryFn = async (data: ReqWorkersNotInSafetyTraining) => {
    return await workerApi.getWorkersNotInSafetyTraining(data);
  };

  return useCommonQuery<
    ReqWorkersNotInSafetyTraining,
    BaseApiResponse<unknown, WorkerInfo>,
    ResultError,
    WorkerInfo[]
  >({
    data,
    options: {
      ...options,
      select: (data) => data.resultList
    },
    queryKeyFactory,
    queryFn
  });
};
